/**
 * The queue's real half: IndexedDB standing in for `QueueStorage`, `fetch`
 * standing in for `QueueSend`, and `navigator.onLine` plus the `online` and
 * `offline` events standing in for `online()`. `src/queue.ts` is the
 * controller these are handed to; this is the one place that reaches for a
 * real browser. `src/theme-document.ts` and `src/motion-document.ts` are the
 * same shape for the same reason.
 *
 * **Why IndexedDB and not `localStorage`.** A queued write's body can be
 * arbitrarily large — a description, a note, a five-way split — and
 * `localStorage` is a synchronous 5 MB budget shared with everything else on
 * the origin; blocking the main thread to serialise the whole queue on every
 * change is the wrong trade for something that exists to survive a phone
 * being put away mid-write. IndexedDB is asynchronous and holds what this
 * needs to hold.
 */

import { createQueueController, type QueueSend, type QueueStorage } from './queue'
import type { QueuedMethod, QueuedWhat, QueuedWrite } from './lib/queue'

const DB_NAME = 'friendloan-queue'
const DB_VERSION = 1
const STORE = 'writes'

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE, { keyPath: 'id' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('could not open the offline queue'))
  })
}

let dbPromise: Promise<IDBDatabase> | null = null
const db = (): Promise<IDBDatabase> => (dbPromise ??= openDb())

function request<T>(run: (store: IDBObjectStore) => IDBRequest<T>, mode: IDBTransactionMode): Promise<T> {
  return db().then(
    (database) =>
      new Promise<T>((resolve, reject) => {
        const tx = database.transaction(STORE, mode)
        const store = tx.objectStore(STORE)
        const req = run(store)
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error ?? new Error('the offline queue could not be read'))
      }),
  )
}

const storage: QueueStorage = {
  all: () => request<QueuedWrite[]>((store) => store.getAll(), 'readonly'),
  put: (write) =>
    request<IDBValidKey>((store) => store.put(write), 'readwrite').then(() => undefined),
  remove: (id) => request<undefined>((store) => store.delete(id), 'readwrite').then(() => undefined),
}

/**
 * Sends one queued write for real. `credentials` is implicit — same-origin —
 * exactly as every other request this app makes; see `src/api.ts`.
 *
 * A response this app cannot make sense of at all (not JSON, or JSON with no
 * `status`) is treated as a server error rather than let through: the write
 * is not confirmed, so `classify` has to be given a status that means "try
 * again", and inventing a refusal out of an unreadable body would stop a
 * retry that a moment later would have worked.
 */
const send: QueueSend = async (write) => {
  const headers: Record<string, string> = { 'Idempotency-Key': write.key }
  if (write.body !== null) headers['content-type'] = 'application/json'

  const res = await fetch(write.path, {
    method: write.method,
    headers,
    body: write.body ?? undefined,
  })

  let body: unknown
  try {
    body = await res.json()
  } catch {
    return { status: res.ok ? 502 : res.status }
  }
  const shape = body as { status?: string; error?: string }
  if (!res.ok || shape?.status !== 'ok') {
    return { status: res.status, code: shape?.error }
  }
  return { status: res.status, body }
}

let onSentHandler: ((write: QueuedWrite, body: unknown) => void) | null = null

const controller = createQueueController({
  storage,
  send,
  online: () => navigator.onLine,
  onSent: (write, body) => onSentHandler?.(write, body),
})

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void controller.drain())
  // A phone that comes back to a signal while the app is in the background
  // gets no `online` event until the tab is looked at again; the tab
  // regaining visibility is the same "maybe there is a network now" signal.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void controller.drain()
  })
}

/** Told once, from `src/main.tsx`, what to do with the caches once a write lands. */
export function setOnSent(handler: (write: QueuedWrite, body: unknown) => void): void {
  onSentHandler = handler
}

export function enqueueWrite(write: {
  id: string
  key: string
  method: QueuedMethod
  path: string
  body: string | null
  what: QueuedWhat
}): Promise<QueuedWrite> {
  return controller.enqueue(write)
}

export function retryQueuedWrite(id: string): Promise<void> {
  return controller.retry(id)
}

export function discardQueuedWrite(id: string): Promise<void> {
  return controller.discard(id)
}

export function listQueuedWrites(): QueuedWrite[] {
  return controller.list()
}

export function subscribeQueue(listener: () => void): () => void {
  return controller.subscribe(listener)
}

export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}
